import { AudioInputStream, ChannelRoutedSource } from "./AudioInputStream";
import { InputProcessorNode, LevelData, RecordingResult, RecordingStartResult } from "./input-processor";
import { WaveformData } from "./types";

export type { LevelData };
export type { RecordingResult };
export type { RecordingStartResult };

export type InputChannel = 'left' | 'right' | 'stereo';

/**
 * Manages audio input for a single device.
 *
 * Uses a single AudioWorklet for all processing:
 * - Level metering (L/R levels for display)
 * - Audio monitoring (hear yourself through speakers)
 * - Recording to PCM audio with realtime waveform
 *
 * @example
 * ```ts
 * const input = new AudioInput();
 *
 * await input.start(audioContext, 'device-id', 'stereo', false, (levels) => {
 *   console.log('Left:', levels.left, 'Right:', levels.right);
 * });
 *
 * // Enable hearing yourself
 * input.setAudioMonitoring(true);
 *
 * // Start recording
 * input.startRecording((waveform) => {
 *   // Update UI with realtime waveform
 * });
 *
 * // Stop recording and get result
 * const { audioBuffer, waveform } = await input.stopRecording();
 *
 * // When done
 * input.stop();
 * ```
 */
export class AudioInput {
  private inputStream: AudioInputStream | null = null;
  private inputSource: ChannelRoutedSource | null = null;
  private processor: InputProcessorNode | null = null;
  private audioMonitoringGain: GainNode | null = null;
  private audioContext: AudioContext | null = null; // TODO-0: remove this unused.
  private currentChannel: InputChannel = 'stereo';
  private _isActive: boolean = false;
  private _channelCount: number | null = null;

  /**
   * Whether input monitoring is currently active.
   */
  get isActive(): boolean {
    return this._isActive;
  }

  /**
   * Whether recording is currently active.
   */
  get isRecording(): boolean {
    return this.processor?.isRecording ?? false;
  }

  /**
   * Number of channels in the input stream (1 for mono, 2 for stereo).
   * Returns null if not currently monitoring.
   */
  get channelCount(): number | null {
    return this._channelCount;
  }

  /**
   * The audio output node (after the worklet).
   * Returns null if not currently monitoring.
   */
  get output(): AudioNode | null {
    return this.processor?.node ?? null;
  }

  /**
   * Register the worklet module.
   * Must be called before starting audio input.
   */
  static async registerWorklet(audioContext: AudioContext): Promise<void> {
    await InputProcessorNode.addModule(audioContext);
  }

  /**
   * Start monitoring input from the specified device.
   *
   * @param audioContext - The AudioContext to use
   * @param deviceId - The device ID to monitor
   * @param channel - Which channel(s) to route for audio monitoring
   * @param audioMonitoringEnabled - Whether to initially enable audio monitoring (hear yourself)
   * @param onLevelChange - Callback for level updates (called at ~60fps)
   */
  async start(
    audioContext: AudioContext,
    deviceId: string,
    channel: InputChannel,
    audioMonitoringEnabled: boolean,
    onLevelChange: (levels: LevelData) => void
  ): Promise<void> {
    // Stop any existing monitoring first
    if (this._isActive) {
      this.stop();
    }

    this.audioContext = audioContext;
    this.currentChannel = channel;

    // Create input stream from device
    this.inputStream = new AudioInputStream(deviceId);
    await this.inputStream.init();

    this._channelCount = this.inputStream.channelCount;

    // Create the input processor worklet (handles level metering + recording)
    this.processor = new InputProcessorNode(audioContext, onLevelChange);

    // Get source from input stream (always stereo for level metering, channel selection happens in recording)
    this.inputSource = this.inputStream.getSource(audioContext, 'stereo');

    // Connect: MediaStream -> Worklet -> Gain -> Destination
    this.inputSource.output.connect(this.processor.node);

    // Audio monitoring gain (0 = muted, 1 = audible)
    this.audioMonitoringGain = audioContext.createGain();
    this.audioMonitoringGain.gain.value = audioMonitoringEnabled ? 1 : 0;
    this.processor.node.connect(this.audioMonitoringGain);
    this.audioMonitoringGain.connect(audioContext.destination);

    this._isActive = true;
  }

  /**
   * Stop monitoring and release resources.
   */
  stop(): void {
    if (!this._isActive) return;

    // Cancel any active recording
    if (this.isRecording) {
      this.cancelRecording();
    }

    if (this.processor) {
      this.processor.destroy();
      this.processor = null;
    }

    if (this.audioMonitoringGain) {
      this.audioMonitoringGain.disconnect();
      this.audioMonitoringGain = null;
    }

    if (this.inputSource) {
      this.inputSource.cleanup();
      this.inputSource = null;
    }

    if (this.inputStream) {
      this.inputStream.stop();
      this.inputStream = null;
    }

    this.audioContext = null;
    this._isActive = false;
    this._channelCount = null;
  }

  /**
   * Enable or disable audio monitoring (hear yourself through speakers).
   *
   * @param enabled - Whether to route input audio to speakers
   */
  setAudioMonitoring(enabled: boolean): void {
    if (this.audioMonitoringGain) {
      this.audioMonitoringGain.gain.value = enabled ? 1 : 0;
    }
  }

  /**
   * Start recording audio from the monitored input.
   *
   * Returns a promise that resolves when the worklet confirms recording has started,
   * providing the exact frame number when capture began.
   *
   * @param onWaveformUpdate - Optional callback for realtime waveform updates
   * @returns Promise that resolves with the recording start timing information
   * @throws Error if monitoring is not active
   */
  startRecording(onWaveformUpdate?: (waveform: WaveformData) => void): Promise<RecordingStartResult> {
    if (!this._isActive || !this.processor) {
      throw new Error('Cannot start recording: input monitoring is not active');
    }
    if (this.isRecording) {
      throw new Error('Already recording');
    }

    return this.processor.startRecording(this.currentChannel, onWaveformUpdate);
  }

  /**
   * Stop recording and return the recorded audio.
   *
   * @returns The recorded audio buffer and waveform data
   * @throws Error if not recording
   */
  async stopRecording(): Promise<RecordingResult> {
    if (!this.processor || !this.isRecording) {
      throw new Error('Not recording');
    }

    return this.processor.stopRecording();
  }

  /**
   * Cancel recording without returning data.
   */
  cancelRecording(): void {
    if (!this.processor || !this.isRecording) return;

    this.processor.cancelRecording();
  }
}
